import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@shared/store/dialog.duck';
import DialogHeader from '@shared/dialogs/DialogHeader';
import Dialog from "@shared/ui/molecules/Dialog";
import { fetchTags, selectTagsLoading } from '@shared/store/userTags.duck';
import { Tag } from '@shared/models/tags.models';
import { TrackJS } from "trackjs";
import { canChangeUsersWorkGroup } from '@shared/utils/AccessControl';
import { selectCurrentUser } from '@shared/store/auth.duck';
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { Column } from "@shared/ui/atoms/StructuralLayout";
import { PrimaryButton } from "@shared/ui/atoms/buttons/PrimaryButton";
import { LoadingOverlay } from "@shared/ui/molecules/LoadingOverlay";
import TextField from "@shared/ui/atoms/TextField";
import { BasicUserInfo } from '@shared/models/user.models';
import { UserAvatarAndName } from "@shared/ui/molecules/UserAvatarAndName";
import { Checkbox } from "@shared/ui/atoms/Checkbox";
import styled from "styled-components";
import { findUserRequest } from '@features/settings/services/UserService';
import { createUserTagRequest, getAllUsersInTag, removeUserTagRequest } from '@shared/services/Tags';
import { BodyRegular } from "@shared/ui/atoms/fonts/Body";
import Colours from "@shared/ui/atoms/Colours";
import { selectCalendarDate, setCalendarDate } from '@shared/store/dashboard.duck';
import moment from "moment";
import { safePrintError } from '@shared/utils/error-helper';

interface Props {
  payload?: { tag: Tag, onClose?: () => void }
}

export default function EditUsersInTagDialog(props: Props) {
  const dispatch = useDispatch();
  const tagsLoading = useSelector(selectTagsLoading);
  const currentUser = useSelector(selectCurrentUser);
  const calendarDate = useSelector(selectCalendarDate);
  const [loading, setLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const hasAccess = useMemo(() => canChangeUsersWorkGroup(currentUser), [currentUser]);

  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<BasicUserInfo[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<BasicUserInfo[]>([]);

  useEffect(() => {
    if (props.payload?.tag) {
      // fetch tag members
      setLoading(true);
      getAllUsersInTag(props.payload.tag.id)
        .then(setSelectedUsers)
        .finally(() => setLoading(false));
    }
  }, [props]);

  useEffect(() => {
    const id = setTimeout(() => {
      findUserRequest(searchQuery)
        .then((result: BasicUserInfo[]) => setUsers(result));
    }, 250);

    return () => {
      clearTimeout(id);
    }
  }, [searchQuery]);

  const close = () => {
    if (hasChanged) {
      dispatch(setCalendarDate(moment(calendarDate)));
      dispatch(fetchTags());
      props.payload?.onClose?.();
    }
    dispatch(closeDialog());
  }

  const onUserSelect = async (selectedUser: BasicUserInfo) => {
    if (!props.payload?.tag) return;
    setLoading(true);
    try {
      if (selectedUsers.includes(selectedUser)) {
        await removeUserTagRequest(selectedUser.userId, props.payload.tag.id);
        setSelectedUsers(selectedUsers.filter(su => su.userId !== selectedUser.userId));
      } else {
        await createUserTagRequest(selectedUser.userId, props.payload.tag.id);
        setSelectedUsers([...selectedUsers, selectedUser]);
      }
      setHasChanged(true);
    } catch (e: any) {
      safePrintError(e, dispatch, 'Failed to add user to group');
      TrackJS?.track(e);
    } finally {
      setLoading(false);
    }
  }

  const onChange = (val: string) => setSearchQuery(val);

  return (
    <Dialog isOpen={true} onClose={close} showLogo={true}>
      <DialogHeader title="dialog-headers.add-group-member" />
      <BodyRegular style={{marginBottom: 16}}>To group: {props.payload?.tag.label ?? '???'}</BodyRegular>
      <AccessRequired teamAdminsRequired={false} companyAdminsRequired={true} visible={!hasAccess} />

      {(loading || tagsLoading) && <LoadingOverlay />}

      {hasAccess && <>
        <SearchBar onIconClick={() => setSearchQuery('')} icon={searchQuery.length > 0 ? "Close" : undefined} value={searchQuery} onChange={onChange} />
        <SearchResults className={"searchDialog__results"}>

          {(searchQuery.length === 0 ? selectedUsers : users).map((user: BasicUserInfo, key: number) => (
            <UserInfoRow key={key} className={'searchDialog__result'}>
              <UserAvatarAndName firstName={user.firstName} lastName={user.lastName} />
              <Checkbox value={!!selectedUsers.find(su => su.userId === user.userId)} onChange={() => onUserSelect(user)} />
            </UserInfoRow>
          ))}

        </SearchResults>
      </>}



      <Column>
        <PrimaryButton fullWidth={true} click={close} text="button.done" />
      </Column>
    </Dialog>
  )
}

const SearchBar = styled(TextField)<any>`
  .icon {
    right: 12px;
    padding: 2px;
    border-radius: 2px;
    transition: background-color .2s;
    cursor: pointer;
    &:hover {
      background-color: ${Colours.opaqueMildGrey};
    }
  }
  .icon--medium {
    width: unset;
    height: unset;
  }
`

const SearchResults = styled.div`
  margin-bottom: 32px;
  height: 250px;
  overflow-y: auto;
`

const UserInfoRow = styled.div<any>`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  .userAvatarAndName {
    cursor: pointer;
  }
`
